import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          /** `invisible` was removed from the Turnstile JS API; use `flexible` + `appearance`. */
          size?: 'normal' | 'compact' | 'flexible';
          /** `execute` keeps the widget hidden until `execute()` runs (on-demand token). */
          appearance?: 'always' | 'execute' | 'interaction-only';
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      /** With `appearance: 'execute'`, call this when you need a token (e.g. OTP confirm). */
      execute: (widgetIdOrContainer?: string | HTMLElement) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/**
 * On-demand Cloudflare Turnstile (`appearance: 'execute'` + `executeTurnstile()`).
 * Do not mount the widget in `display:none`; it needs a real layout box (see `turnstileContainerClass`).
 */
export const TURNSTILE_CONTAINER_CLASS =
  'pointer-events-none fixed left-0 top-0 z-[-1] h-[70px] w-[min(100vw,300px)] opacity-0 overflow-hidden';

export function useTurnstile() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  /** Single-flight guard: if execute is already running, reuse the same promise. */
  const executeInFlightRef = useRef<Promise<string | null> | null>(null);
  /** Tracks Turnstile widget internal execution state to avoid execute-while-busy races. */
  const widgetBusyRef = useRef(false);
  /** Set by `executeTurnstile` while waiting for Cloudflare callback (or timeout). */
  const tokenPendingRef = useRef<((t: string | null) => void) | null>(null);
  /** Latest `remove` + `render` from the mount effect (for `remountTurnstile` after OTP attempts). */
  const remountFnRef = useRef<() => void>(() => {});

  const finishToken = useCallback((t: string | null) => {
    widgetBusyRef.current = false;
    setToken(t);
    const pending = tokenPendingRef.current;
    tokenPendingRef.current = null;
    pending?.(t);
  }, []);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;

    const tryRender = () => {
      if (!window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current !== null) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (t: string) => finishToken(t),
        'expired-callback': () => finishToken(null),
        'error-callback': () => {
          finishToken(null);
        },
        size: 'flexible',
        appearance: 'execute',
        theme: 'auto',
      });
    };

    const removeWidget = () => {
      widgetBusyRef.current = false;
      const pending = tokenPendingRef.current;
      tokenPendingRef.current = null;
      pending?.(null);
      const id = widgetIdRef.current;
      if (id != null && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* ignore */
        }
      }
      widgetIdRef.current = null;
      setToken(null);
    };

    const remount = () => {
      removeWidget();
      if (!SITE_KEY || !containerRef.current) return;
      if (window.turnstile) {
        tryRender();
      }
    };

    remountFnRef.current = remount;

    let interval: number | undefined;
    if (window.turnstile) {
      tryRender();
    } else {
      interval = window.setInterval(() => {
        if (window.turnstile) {
          if (interval != null) window.clearInterval(interval);
          interval = undefined;
          tryRender();
        }
      }, 200);
    }

    return () => {
      if (interval != null) window.clearInterval(interval);
      removeWidget();
      remountFnRef.current = () => {};
    };
  }, [finishToken]);

  const resetTurnstile = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  /** `remove` + `render` so the next `executeTurnstile()` gets a new widget (fixes stale / reused tokens). */
  const remountTurnstile = useCallback(() => {
    remountFnRef.current();
  }, []);

  /**
   * Runs the challenge and resolves when a token is ready (or null on failure / timeout).
   * Includes busy-state + remount retry to avoid "already executing" races.
   */
  const executeTurnstile = useCallback((): Promise<string | null> => {
    if (!SITE_KEY || widgetIdRef.current === null || !window.turnstile?.execute) {
      return Promise.resolve(null);
    }
    if (executeInFlightRef.current) {
      return executeInFlightRef.current;
    }
    const executeOnce = (): Promise<string | null> =>
      new Promise<string | null>((resolve) => {
        const staleResolve = tokenPendingRef.current;
        tokenPendingRef.current = null;
        staleResolve?.(null);

        const tid = window.setTimeout(() => {
          widgetBusyRef.current = false;
          tokenPendingRef.current = null;
          resolve(null);
        }, 25_000);

        const done = (t: string | null) => {
          window.clearTimeout(tid);
          resolve(t);
        };

        tokenPendingRef.current = done;
        const widgetId = widgetIdRef.current;
        if (!widgetId || !window.turnstile?.execute) {
          widgetBusyRef.current = false;
          window.clearTimeout(tid);
          tokenPendingRef.current = null;
          resolve(null);
          return;
        }
        try {
          widgetBusyRef.current = true;
          window.turnstile.execute(widgetId);
        } catch {
          widgetBusyRef.current = false;
          window.clearTimeout(tid);
          tokenPendingRef.current = null;
          resolve(null);
        }
      });

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, ms));

    const execution = (async (): Promise<string | null> => {
      // If widget still marked busy (stale browser state), remount before executing.
      if (widgetBusyRef.current) {
        remountFnRef.current();
        await sleep(140);
      }

      let token = await executeOnce();
      if (token) return token;

      // One recovery retry on null: remount and execute again.
      remountFnRef.current();
      await sleep(140);
      token = await executeOnce();
      return token;
    })();

    executeInFlightRef.current = execution;
    void execution.finally(() => {
      if (executeInFlightRef.current === execution) {
        executeInFlightRef.current = null;
      }
      widgetBusyRef.current = false;
    });
    return execution;
  }, []);

  return {
    token,
    resetTurnstile,
    remountTurnstile,
    executeTurnstile,
    turnstileRef: containerRef,
    turnstileEnabled: !!SITE_KEY,
  };
}
